import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ImageUploader from '../components/ImageUploader';
import RichTextEditor from '../components/RichTextEditor';
import ShareButton from '../components/ShareButton';
import EditablePageIntro from '../components/EditablePageIntro';
import DisplayPrice from '../components/DisplayPrice';
import { resolveImageUrl } from '../utils/imageUrl';
import { BookOpen, FileText, Video, Headphones, Download, Search, Plus, Lock, ShoppingCart, X, Edit2, Trash2, Loader2, Info, ArrowRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { CURRENCY_OPTIONS, currencySymbol, formatPrice } from '../utils/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
    { id: 'all', name: 'All Resources' },
    { id: 'books', name: 'Books' },
    { id: 'videos', name: 'Videos' },
    { id: 'audio', name: 'Audio' },
    { id: 'tools', name: 'Tools' },
    { id: 'guides', name: 'Guides' }
];

const getContentIcon = (type) => {
    switch (type) {
        case 'video': return Video;
        case 'audio': return Headphones;
        case 'ebook':
        case 'pdf': return FileText;
        default: return BookOpen;
    }
};

const CONTENT_TYPES = [
    { value: 'ebook', label: 'E-book' },
    { value: 'book', label: 'Book (physical)' },
    { value: 'audio', label: 'Audio' },
    { value: 'video', label: 'Video' },
    { value: 'tool', label: 'Tool' },
    { value: 'guide', label: 'Guide' }
];

export default function LibraryPage() {
    const { user, isAuthenticated } = useAuth();
    const [searchParams] = useSearchParams();
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    // View toggle: browse the whole library or just what I've bought / can access.
    const [viewMode, setViewMode] = useState('all'); // 'all' | 'mine'
    
    // Add/Edit resource
    const [addResourceOpen, setAddResourceOpen] = useState(false);
    const [editingResource, setEditingResource] = useState(null);
    const [newResource, setNewResource] = useState({
        title: '',
        description: '',
        category: 'books',
        content_type: 'ebook',
        content_url: '',
        content_file_id: null,
        content_file_meta: null, // {filename, size} — kept locally for the "current file" hint
        image_url: '',
        price: 0,
        member_price: 0,
        currency: 'eur',
        is_members_only: false,
        min_tier: 'walker'
    });
    const [uploadingFile, setUploadingFile] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    
    // Resource details
    const [selectedResource, setSelectedResource] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [purchasing, setPurchasing] = useState(false);
    const [guestName, setGuestName] = useState('');
    const [guestEmail, setGuestEmail] = useState('');
    const [guestEmailConflict, setGuestEmailConflict] = useState(null);
    const [wantsSeekerUpsell, setWantsSeekerUpsell] = useState(false);
    const [seekerUpsellPrice, setSeekerUpsellPrice] = useState(1);

    // Check if user can add resources (admin or partnership)
    const canManageResources = user?.role === 'admin' || user?.membership_tier === 'partnership';
    
    // Check if user has member pricing
    const hasMemberPricing = user?.membership_tier && ['walker', 'architect', 'partnership'].includes(user.membership_tier);

    useEffect(() => {
        fetchResources();
        // Pull upsell price from site settings (public endpoint)
        axios.get(`${API_URL}/api/site-settings`).then(({ data }) => {
            const p = Number(data?.seeker_upsell_price_eur);
            if (Number.isFinite(p) && p > 0) setSeekerUpsellPrice(p);
        }).catch(() => {});
        
        // Check for purchase success — verify session and mark resource purchased
        const purchaseSuccess = searchParams.get('purchase_success');
        const sessionId = searchParams.get('session_id');
        if (purchaseSuccess === 'true' && sessionId) {
            pollPurchaseStatus(sessionId);
        }
    }, [selectedCategory, searchParams]);

    // Auto-open a resource detail modal when a deep-link like /library?open=<id>
    // is used (from Share links, newsletters, etc.). Runs whenever the
    // resource list refreshes so it works even before login redirects settle.
    useEffect(() => {
        const openId = searchParams.get('open');
        if (!openId || !resources.length) return;
        const target = resources.find(r => r.id === openId);
        if (target) {
            setSelectedResource(target);
            setDetailsOpen(true);
        }
    }, [resources, searchParams]);

    const pollPurchaseStatus = async (sessionId, attempts = 0) => {
        const maxAttempts = 8;
        if (attempts >= maxAttempts) {
            toast.info('Purchase is being processed. Refresh in a moment.');
            window.history.replaceState({}, document.title, '/library');
            return;
        }
        try {
            const { data } = await axios.get(`${API_URL}/api/library/purchase/status/${sessionId}`, { withCredentials: true });
            if (data.payment_status === 'paid') {
                toast.success('Purchase successful! Your resource is now accessible.');
                window.history.replaceState({}, document.title, '/library');
                // Reload the list, switch to My Library, and open the purchased item.
                setViewMode('mine');
                const list = await fetchResources();
                const bought = (list || []).find(r => r.id === data.resource_id);
                if (bought) {
                    setSelectedResource(bought);
                    setDetailsOpen(true);
                }
                return;
            }
            if (data.status === 'expired') {
                toast.error('Payment session expired. Please try again.');
                window.history.replaceState({}, document.title, '/library');
                return;
            }
            setTimeout(() => pollPurchaseStatus(sessionId, attempts + 1), 2000);
        } catch (error) {
            setTimeout(() => pollPurchaseStatus(sessionId, attempts + 1), 2000);
        }
    };

    const fetchResources = async () => {
        try {
            const params = new URLSearchParams();
            if (selectedCategory && selectedCategory !== 'all') {
                params.append('category', selectedCategory);
            }
            
            const { data } = await axios.get(`${API_URL}/api/library?${params}`, { withCredentials: true });
            setResources(data);
            return data;
        } catch (error) {
            console.error('Error fetching resources:', error);
            toast.error('Failed to load resources');
            return [];
        } finally {
            setLoading(false);
        }
    };

    const handleAddResource = async (e) => {
        e.preventDefault();
        if (!newResource.title || (!newResource.content_url && !newResource.content_file_id)) {
            toast.error('Please fill in all required fields');
            return;
        }
        setSubmitting(true);
        try {
            if (editingResource) {
                await axios.put(`${API_URL}/api/library/${editingResource.id}`, newResource, { withCredentials: true });
                toast.success('Resource updated successfully!');
            } else {
                await axios.post(`${API_URL}/api/library`, newResource, { withCredentials: true });
                toast.success('Resource added successfully!');
            }
            setAddResourceOpen(false);
            setEditingResource(null);
            resetForm();
            fetchResources();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save resource');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteResource = async (resourceId) => {
        if (!window.confirm('Are you sure you want to delete this resource?')) return;
        
        try {
            await axios.delete(`${API_URL}/api/library/${resourceId}`, { withCredentials: true });
            toast.success('Resource deleted');
            fetchResources();
        } catch (error) {
            toast.error('Failed to delete resource');
        }
    };

    // Check whether the guest's email is already registered so we can offer sign-in.
    const checkGuestEmail = async () => {
        const e = (guestEmail || '').trim().toLowerCase();
        if (!e || isAuthenticated) return;
        try {
            const { data } = await axios.get(`${API_URL}/api/auth/check-email`, { params: { email: e } });
            setGuestEmailConflict(data.exists ? { name: data.name } : null);
        } catch (_err) {
            setGuestEmailConflict(null);
        }
    };

    const handlePurchase = async () => {
        if (!selectedResource) return;
        
        if (!isAuthenticated && !guestEmail) {
            toast.error('Please enter your email address');
            return;
        }
        if (!isAuthenticated && guestEmailConflict) {
            toast.error('An account already exists — please sign in first.');
            return;
        }
        
        setPurchasing(true);
        try {
            const { data } = await axios.post(
                `${API_URL}/api/library/${selectedResource.id}/checkout`,
                {
                    resource_id: selectedResource.id,
                    origin_url: window.location.origin,
                    name: guestName || undefined,
                    email: guestEmail || undefined,
                    wants_seeker_upsell: !isAuthenticated && wantsSeekerUpsell,
                },
                { withCredentials: true }
            );

            if (data.free) {
                toast.success(data.already_owned ? 'You already own this.' : 'Added to your library. Check your inbox (and spam) for the details.');
                setDetailsOpen(false);
                setPurchasing(false);
                fetchResources();
                return;
            }

            window.location.href = data.url;
        } catch (error) {
            const detail = error.response?.data?.detail;
            if (error.response?.status === 409 && typeof detail === 'object' && detail?.code === 'account_exists') {
                setGuestEmailConflict({ name: detail.name || '' });
                toast.error(detail.message || 'Please sign in first.');
            } else {
                toast.error((typeof detail === 'string' ? detail : detail?.message) || 'Failed to start checkout');
            }
            setPurchasing(false);
        }
    };

    const resetForm = () => {
        setNewResource({
            title: '',
            description: '',
            category: 'books',
            content_type: 'ebook',
            content_url: '',
            content_file_id: null,
            content_file_meta: null,
            image_url: '',
            price: 0,
            member_price: 0,
            currency: 'eur',
            is_members_only: false,
            min_tier: 'walker'
        });
    };

    const openEditDialog = (resource) => {
        setEditingResource(resource);
        setNewResource({
            title: resource.title,
            description: resource.description || '',
            category: resource.category,
            content_type: resource.content_type,
            content_url: resource.content_url || '',
            content_file_id: resource.content_file_id || null,
            content_file_meta: resource.content_file_id ? { filename: 'Uploaded file (kept)', size: 0 } : null,
            image_url: resource.image_url || '',
            price: resource.price || 0,
            member_price: resource.member_price || 0,
            currency: resource.currency || 'eur',
            is_members_only: resource.is_members_only || false,
            min_tier: resource.min_tier || 'walker'
        });
        setAddResourceOpen(true);
    };

    const openDetailsDialog = (resource) => {
        setSelectedResource(resource);
        setGuestEmail('');
        setDetailsOpen(true);
    };

    // Ask the backend for a fresh signed URL for hosted files or the external
    // link for URL-based resources. Guests without an account get a helpful
    // toast — they should use the download link from their purchase email.
    const [accessing, setAccessing] = useState(null); // resource_id currently loading
    const handleAccessResource = async (resource) => {
        if (!resource) return;
        if (!isAuthenticated) {
            toast.info('Please sign in, or use the download link we sent to your email.');
            return;
        }
        try {
            setAccessing(resource.id);
            const { data } = await axios.get(
                `${API_URL}/api/library/${resource.id}/access-link`,
                { withCredentials: true }
            );
            if (!data?.url) throw new Error('empty');
            const absoluteUrl = data.url.startsWith('http')
                ? data.url
                : `${API_URL}${data.url}`;
            // Force a same-tab navigation for hosted downloads so the browser
            // triggers the "Save file" dialog; open external links in a new tab.
            if (data.is_download) {
                window.location.href = absoluteUrl;
            } else {
                window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
            }
        } catch (err) {
            const msg = err?.response?.data?.detail || 'Could not open this resource.';
            toast.error(msg);
        } finally {
            setAccessing(null);
        }
    };

    const filteredResources = resources
        .filter(resource =>
            resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (resource.description && resource.description.toLowerCase().includes(searchQuery.toLowerCase()))
        )
        .filter(resource => {
            if (viewMode !== 'mine') return true;
            // "My Library" = anything the user already owns OR free content they can access.
            if (resource.purchased) return true;
            const price = hasMemberPricing && resource.member_price !== undefined ? resource.member_price : (resource.price || 0);
            return price === 0 && resource.accessible !== false;
        });

    const myLibraryCount = resources.filter(r => {
        if (r.purchased) return true;
        const price = hasMemberPricing && r.member_price !== undefined ? r.member_price : (r.price || 0);
        return price === 0 && r.accessible !== false;
    }).length;

    const getPrice = (resource) => {
        if (hasMemberPricing && resource.member_price !== undefined) {
            return resource.member_price;
        }
        return resource.price || 0;
    };

    return (
        <div className="min-h-screen bg-[#F9F6F0]">
            <Navbar />

            <main className="pt-24 pb-16" data-testid="library-page">
                <div className="max-w-7xl mx-auto px-6 sm:px-12">
                    {/* Header */}
                    <div className="text-center mb-16">
                        <p className="overline mb-4">Resources</p>
                        <h1 className="heading-2 mb-6">Library</h1>
                        <EditablePageIntro
                            pageKey="library"
                            className="max-w-2xl mx-auto mb-8"
                            fallback={(
                                <p className="body-text">
                                    Access our collection of books, videos, audio, and tools to support your journey.
                                    {!hasMemberPricing && (
                                        <span className="block mt-2 text-[#5B2D8E]">
                                            Members (Walker+) enjoy special pricing on all resources!
                                        </span>
                                    )}
                                </p>
                            )}
                        />

                        {/* Add Resource Button for Partnership/Admin */}
                        {canManageResources && (
                            <Dialog open={addResourceOpen} onOpenChange={(open) => {
                                setAddResourceOpen(open);
                                if (!open) {
                                    setEditingResource(null);
                                    resetForm();
                                }
                            }}>
                                <DialogTrigger asChild>
                                    <button className="btn-primary flex items-center gap-2 mx-auto" data-testid="add-resource-btn">
                                        <Plus className="w-4 h-4" />
                                        Add Resource
                                    </button>
                                </DialogTrigger>
                                <DialogContent className="sm:max-w-lg bg-white max-h-[90vh] overflow-y-auto">
                                    <DialogHeader>
                                        <DialogTitle className="font-serif text-2xl text-[#1A1A2E]">
                                            {editingResource ? 'Edit Resource' : 'Add New Resource'}
                                        </DialogTitle>
                                    </DialogHeader>
                                    <form onSubmit={handleAddResource} className="space-y-4 mt-4" data-testid="add-resource-form">
                                        <div>
                                            <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Title *</label>
                                            <input
                                                type="text"
                                                value={newResource.title}
                                                onChange={(e) => setNewResource({ ...newResource, title: e.target.value })}
                                                className="w-full"
                                                required
                                                data-testid="resource-title"
                                            />
                                        </div>
                                        <div>
                                            <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Description</label>
                                            <RichTextEditor
                                                value={newResource.description}
                                                onChange={(html) => setNewResource({ ...newResource, description: html })}
                                                placeholder="What buyers will get. This shows in the item card, the details modal, and the confirmation email."
                                                data-testid="resource-description"
                                            />
                                        </div>
                                        <div>
                                            <ImageUploader
                                                value={newResource.image_url}
                                                onChange={(url) => setNewResource({ ...newResource, image_url: url })}
                                                testIdPrefix="resource-image"
                                                label="Cover Image"
                                                aspect="video"
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Category</label>
                                                <select
                                                    value={newResource.category}
                                                    onChange={(e) => setNewResource({ ...newResource, category: e.target.value })}
                                                    className="w-full bg-white border border-[#E5E0D8] px-4 py-3 rounded-sm"
                                                    data-testid="resource-category"
                                                >
                                                    <option value="books">Books</option>
                                                    <option value="videos">Videos</option>
                                                    <option value="audio">Audio</option>
                                                    <option value="tools">Tools</option>
                                                    <option value="guides">Guides</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Content Type</label>
                                                <select
                                                    value={newResource.content_type}
                                                    onChange={(e) => setNewResource({ ...newResource, content_type: e.target.value })}
                                                    className="w-full bg-white border border-[#E5E0D8] px-4 py-3 rounded-sm"
                                                    data-testid="resource-type"
                                                >
                                                    {CONTENT_TYPES.map(t => (
                                                        <option key={t.value} value={t.value}>{t.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="border border-[#E5E0D8] rounded-sm p-4 bg-[#F9F6F0]/60">
                                            <p className="text-xs uppercase tracking-widest text-[#5B2D8E] mb-3">How buyers get the item — pick one or both</p>

                                            <div className="mb-4">
                                                <label className="block font-sans text-sm text-[#1A1A2E] mb-2">1. External link (YouTube, Notion, Google Drive…)</label>
                                                <input
                                                    type="url"
                                                    value={newResource.content_url}
                                                    onChange={(e) => setNewResource({ ...newResource, content_url: e.target.value })}
                                                    className="w-full"
                                                    placeholder="https://…"
                                                    data-testid="resource-url"
                                                />
                                            </div>

                                            <div>
                                                <label className="block font-sans text-sm text-[#1A1A2E] mb-2">2. Upload the file to the website (PDF, mp3, mp4, epub, zip — up to 100 MB)</label>
                                                {newResource.content_file_id ? (
                                                    <div className="flex items-center justify-between gap-3 p-3 bg-white border border-[#E5E0D8] rounded-sm" data-testid="resource-file-current">
                                                        <div className="text-sm">
                                                            <p className="text-[#1A1A2E]">
                                                                <strong>{newResource.content_file_meta?.filename || 'Uploaded file'}</strong>
                                                            </p>
                                                            {newResource.content_file_meta?.size > 0 && (
                                                                <p className="text-xs text-[#4A4A68]">
                                                                    {(newResource.content_file_meta.size / (1024 * 1024)).toFixed(1)} MB
                                                                </p>
                                                            )}
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => setNewResource({ ...newResource, content_file_id: null, content_file_meta: null })}
                                                            className="text-xs text-[#5B2D8E] hover:underline"
                                                            data-testid="resource-file-remove"
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <label className="flex items-center gap-3 p-3 bg-white border border-dashed border-[#5B2D8E]/40 rounded-sm cursor-pointer hover:border-[#5B2D8E]">
                                                        <input
                                                            type="file"
                                                            accept=".pdf,.mp3,.m4a,.wav,.aac,.ogg,.mp4,.mov,.m4v,.webm,.epub,.mobi,.zip,.jpg,.jpeg,.png,.webp"
                                                            onChange={async (e) => {
                                                                const f = e.target.files?.[0];
                                                                if (!f) return;
                                                                if (f.size > 100 * 1024 * 1024) {
                                                                    toast.error('File is too large (max 100 MB).');
                                                                    return;
                                                                }
                                                                setUploadingFile(true);
                                                                try {
                                                                    const fd = new FormData();
                                                                    fd.append('file', f);
                                                                    const { data } = await axios.post(`${API_URL}/api/upload/library-file`, fd, {
                                                                        withCredentials: true,
                                                                        headers: { 'Content-Type': 'multipart/form-data' },
                                                                    });
                                                                    setNewResource((prev) => ({
                                                                        ...prev,
                                                                        content_file_id: data.file_id,
                                                                        content_file_meta: { filename: data.filename, size: data.size },
                                                                    }));
                                                                    toast.success('File uploaded — buyers will get it in their confirmation email.');
                                                                } catch (err) {
                                                                    toast.error(err.response?.data?.detail || 'Upload failed');
                                                                } finally {
                                                                    setUploadingFile(false);
                                                                    e.target.value = '';
                                                                }
                                                            }}
                                                            className="hidden"
                                                            data-testid="resource-file-input"
                                                        />
                                                        <span className="text-sm text-[#5B2D8E]">
                                                            {uploadingFile ? 'Uploading…' : 'Choose a file to upload'}
                                                        </span>
                                                    </label>
                                                )}
                                                <p className="text-xs text-[#4A4A68] mt-1">Buyers get a private 7-day download link in their email — no account needed.</p>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Currency</label>
                                            <select
                                                value={newResource.currency}
                                                onChange={(e) => setNewResource({ ...newResource, currency: e.target.value })}
                                                className="w-full"
                                                data-testid="resource-currency"
                                            >
                                                {CURRENCY_OPTIONS.map(c => (
                                                    <option key={c.value} value={c.value}>{c.label}</option>
                                                ))}
                                            </select>
                                            <p className="text-xs text-[#4A4A68] mt-1">Visitors are charged in this currency.</p>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Regular Price ({currencySymbol(newResource.currency)})</label>
                                                <input
                                                    type="number"
                                                    value={newResource.price}
                                                    onChange={(e) => setNewResource({ ...newResource, price: parseFloat(e.target.value) || 0 })}
                                                    className="w-full"
                                                    min="0"
                                                    step="0.01"
                                                    data-testid="resource-price"
                                                />
                                                <p className="text-xs text-[#4A4A68] mt-1">Set to 0 for free</p>
                                            </div>
                                            <div>
                                                <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Member Price ({currencySymbol(newResource.currency)})</label>
                                                <input
                                                    type="number"
                                                    value={newResource.member_price}
                                                    onChange={(e) => setNewResource({ ...newResource, member_price: parseFloat(e.target.value) || 0 })}
                                                    className="w-full"
                                                    min="0"
                                                    step="0.01"
                                                    data-testid="resource-member-price"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-3 p-4 bg-[#F9F6F0] rounded-sm">
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    id="is_members_only"
                                                    checked={newResource.is_members_only}
                                                    onChange={(e) => setNewResource({ ...newResource, is_members_only: e.target.checked })}
                                                    className="w-4 h-4 accent-[#5B2D8E]"
                                                    data-testid="resource-members-only"
                                                />
                                                <label htmlFor="is_members_only" className="font-sans text-sm text-[#4A4A68]">
                                                    Members only (restrict access)
                                                </label>
                                            </div>
                                            {newResource.is_members_only && (
                                                <div>
                                                    <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Minimum Tier Required</label>
                                                    <select
                                                        value={newResource.min_tier}
                                                        onChange={(e) => setNewResource({ ...newResource, min_tier: e.target.value })}
                                                        className="w-full bg-white border border-[#E5E0D8] px-4 py-3 rounded-sm"
                                                    >
                                                        <option value="seeker">Seeker</option>
                                                        <option value="walker">Walker</option>
                                                        <option value="architect">Architect</option>
                                                    </select>
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            type="submit"
                                            className="btn-primary w-full"
                                            disabled={submitting}
                                            data-testid="submit-resource-btn"
                                        >
                                            {submitting ? 'Saving...' : (editingResource ? 'Update Resource' : 'Add Resource')}
                                        </button>
                                    </form>
                                </DialogContent>
                            </Dialog>
                        )}
                    </div>

                    {/* Search and Filter */}
                    <div className="flex flex-col sm:flex-row gap-4 mb-8">
                        {/* Search */}
                        <div className="relative flex-grow">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4A4A68]" />
                            <input
                                type="text"
                                placeholder="Search resources..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-12"
                                data-testid="library-search"
                            />
                        </div>
                    </div>

                    {/* View toggle: browse everything or just my library */}
                    {isAuthenticated && (
                        <div className="flex justify-center mb-6" data-testid="library-view-toggle">
                            <div className="inline-flex bg-white border border-[#E5E0D8] rounded-sm overflow-hidden">
                                <button
                                    type="button"
                                    onClick={() => setViewMode('all')}
                                    className={`px-6 py-2 text-sm uppercase tracking-widest transition ${viewMode === 'all' ? 'bg-[#5B2D8E] text-white' : 'text-[#4A4A68] hover:bg-[#F9F6F0]'}`}
                                    data-testid="library-view-all"
                                >
                                    Browse all
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setViewMode('mine')}
                                    className={`px-6 py-2 text-sm uppercase tracking-widest transition ${viewMode === 'mine' ? 'bg-[#5B2D8E] text-white' : 'text-[#4A4A68] hover:bg-[#F9F6F0]'}`}
                                    data-testid="library-view-mine"
                                >
                                    My library
                                    {myLibraryCount > 0 && (
                                        <span className={`ml-2 text-xs ${viewMode === 'mine' ? 'text-white/80' : 'text-[#5B2D8E]'}`}>
                                            ({myLibraryCount})
                                        </span>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Category Tabs */}
                    <div className="flex gap-2 flex-wrap mb-8">
                        {CATEGORIES.map(cat => (
                            <button
                                key={cat.id}
                                onClick={() => setSelectedCategory(cat.id)}
                                className={`px-4 py-2 rounded-sm text-sm font-sans transition-all ${
                                    selectedCategory === cat.id
                                        ? 'bg-[#5B2D8E] text-white'
                                        : 'bg-white border border-[#E5E0D8] text-[#4A4A68] hover:border-[#5B2D8E]'
                                }`}
                                data-testid={`category-${cat.id}`}
                            >
                                {cat.name}
                            </button>
                        ))}
                    </div>

                    {/* Resources Grid */}
                    {loading ? (
                        <div className="text-center py-12">
                            <div className="w-12 h-12 border-4 border-[#5B2D8E] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                            <p className="text-[#4A4A68]">Loading resources...</p>
                        </div>
                    ) : filteredResources.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="resources-grid">
                            {filteredResources.map(resource => {
                                const Icon = getContentIcon(resource.content_type);
                                const price = getPrice(resource);
                                const isFree = price === 0;
                                const isPurchased = !!resource.purchased;
                                // True tier-lock: backend set `required_tier` when the user's tier is BELOW min_tier.
                                // Otherwise accessible=false just means "paid, not yet purchased" → still buyable.
                                const isTierLocked = !resource.accessible && !!resource.required_tier;
                                const canAccessNow = isPurchased || (isFree && resource.accessible);
                                const isRestricted = isTierLocked;
                                
                                return (
                                    <div
                                        key={resource.id}
                                        className={`bg-white border border-[#E5E0D8] rounded-sm overflow-hidden hover:shadow-lg transition-shadow ${isRestricted ? 'opacity-75' : ''}`}
                                        data-testid={`resource-${resource.id}`}
                                    >
                                        {/* Image */}
                                        <div className="h-40 bg-gradient-to-br from-[#5B2D8E]/10 to-[#D8B4E2]/20 relative flex items-center justify-center">
                                            {resource.image_url ? (
                                                <img src={resolveImageUrl(resource.image_url)} alt={resource.title} className="w-full h-full object-cover" />
                                            ) : (
                                                <Icon className="w-16 h-16 text-[#5B2D8E]/30" />
                                            )}
                                            {isRestricted && (
                                                <div className="absolute inset-0 bg-[#1A1A2E]/50 flex items-center justify-center">
                                                    <div className="text-center text-white">
                                                        <Lock className="w-8 h-8 mx-auto mb-2" />
                                                        <p className="text-sm uppercase">{resource.required_tier}+ only</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="p-6">
                                            <div className="flex items-start justify-between gap-2 mb-2">
                                                <span className="text-xs uppercase tracking-wider text-[#5B2D8E] font-medium">
                                                    {resource.category}
                                                </span>
                                                {canManageResources && (user?.role === 'admin' || String(resource.created_by) === String(user?.id)) && (
                                                    <div className="flex gap-1">
                                                        <button
                                                            onClick={() => openEditDialog(resource)}
                                                            className="p-1 hover:bg-[#F2EBE5] rounded"
                                                            data-testid={`edit-${resource.id}`}
                                                            title="Edit"
                                                        >
                                                            <Edit2 className="w-4 h-4 text-[#4A4A68]" />
                                                        </button>
                                                        <button
                                                            onClick={async () => {
                                                                try {
                                                                    await axios.post(`${API_URL}/api/library/${resource.id}/copy`, {}, { withCredentials: true });
                                                                    toast.success('Resource duplicated');
                                                                    fetchResources();
                                                                } catch (err) {
                                                                    toast.error('Copy failed');
                                                                }
                                                            }}
                                                            className="p-1 hover:bg-[#F2EBE5] rounded"
                                                            data-testid={`copy-${resource.id}`}
                                                            title="Duplicate"
                                                        >
                                                            <svg className="w-4 h-4 text-[#4A4A68]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2v-2m-6-12h6a2 2 0 012 2v8a2 2 0 01-2 2H10a2 2 0 01-2-2V7a2 2 0 012-2z" /></svg>
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteResource(resource.id)}
                                                            className="p-1 hover:bg-red-50 rounded"
                                                            data-testid={`delete-${resource.id}`}
                                                            title="Delete"
                                                        >
                                                            <Trash2 className="w-4 h-4 text-red-500" />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            
                                            <h3 className="font-serif text-xl text-[#1A1A2E] mb-1">
                                                {resource.title}
                                            </h3>
                                            {resource.creator_name && (
                                                <p className="text-xs text-[#4A4A68] mb-2" data-testid={`resource-creator-${resource.id}`}>
                                                    by{' '}
                                                    {resource.creator_is_partnership && resource.creator_id ? (
                                                        <Link
                                                            to={`/pillars/${resource.creator_id}`}
                                                            className="text-[#5B2D8E] hover:underline"
                                                        >
                                                            {resource.creator_name}
                                                        </Link>
                                                    ) : (
                                                        <span>{resource.creator_name}</span>
                                                    )}
                                                </p>
                                            )}

                                            <div
                                                className="font-sans text-sm text-[#4A4A68] mb-4 line-clamp-2 [&_p]:m-0 [&_a]:text-[#5B2D8E]"
                                                dangerouslySetInnerHTML={{ __html: resource.description || '' }}
                                            />

                                            {/* Pricing */}
                                            <div className="mb-4 p-3 bg-[#F9F6F0] rounded-sm">
                                                {isFree ? (
                                                    <p className="font-serif text-xl text-[#5B2D8E]">Free</p>
                                                ) : (
                                                    <div className="flex items-baseline justify-between">
                                                        <DisplayPrice amount={price} currency={resource.currency} className="font-serif text-xl text-[#1A1A2E]" />
                                                        {!hasMemberPricing && resource.member_price < resource.price && (
                                                            <DisplayPrice amount={resource.member_price} currency={resource.currency} className="text-xs text-[#5B2D8E] items-end" showNote={false} />
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => openDetailsDialog(resource)}
                                                    className="flex-1 btn-secondary text-xs px-4 py-3 flex items-center justify-center gap-2"
                                                    data-testid={`details-${resource.id}`}
                                                >
                                                    <Info className="w-4 h-4" />
                                                    Details
                                                </button>
                                                {!isRestricted && (
                                                    canAccessNow ? (
                                                        <button
                                                            onClick={() => handleAccessResource(resource)}
                                                            disabled={accessing === resource.id}
                                                            className="flex-1 btn-primary text-xs px-4 py-3 flex items-center justify-center gap-2 disabled:opacity-60"
                                                            data-testid={`access-${resource.id}`}
                                                        >
                                                            {accessing === resource.id ? (
                                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                            ) : (
                                                                <Download className="w-4 h-4" />
                                                            )}
                                                            Access
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={() => openDetailsDialog(resource)}
                                                            className="flex-1 btn-primary text-xs px-4 py-3 flex items-center justify-center gap-2"
                                                            data-testid={`buy-${resource.id}`}
                                                        >
                                                            <ShoppingCart className="w-4 h-4" />
                                                            Buy Now
                                                        </button>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-16 bg-white border border-[#E5E0D8] rounded-sm" data-testid="no-resources">
                            <BookOpen className="w-12 h-12 text-[#D8B4E2] mx-auto mb-4" />
                            <h3 className="font-serif text-xl text-[#1A1A2E] mb-2">No resources found</h3>
                            <p className="text-[#4A4A68]">Try adjusting your search or filter.</p>
                        </div>
                    )}
                </div>
            </main>

            {/* Resource Details Dialog */}
            <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
                <DialogContent className="sm:max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
                    {selectedResource && (
                        <>
                            <DialogHeader>
                                <div className="flex items-start justify-between gap-3">
                                    <DialogTitle className="font-serif text-2xl text-[#1A1A2E]">
                                        {selectedResource.title}
                                    </DialogTitle>
                                    <ShareButton
                                        url={`${window.location.origin}/library?open=${selectedResource.id}`}
                                        title={selectedResource.title}
                                        text={`Discover "${selectedResource.title}" on Methode 4 Life`}
                                        size="sm"
                                        variant="outline"
                                        className="shrink-0 mr-6"
                                        testIdPrefix={`library-header-share-${selectedResource.id}`}
                                    />
                                </div>
                            </DialogHeader>
                            
                            <div className="mt-4 space-y-6">
                                {/* Image */}
                                {selectedResource.image_url && (
                                    <img 
                                        src={resolveImageUrl(selectedResource.image_url)} 
                                        alt={selectedResource.title}
                                        className="w-full h-64 object-cover rounded-sm"
                                    />
                                )}

                                {/* Category & Type */}
                                <div className="flex gap-4">
                                    <span className="px-3 py-1 bg-[#F2EBE5] text-[#5B2D8E] text-sm rounded-sm capitalize">
                                        {selectedResource.category}
                                    </span>
                                    <span className="px-3 py-1 bg-[#F2EBE5] text-[#5B2D8E] text-sm rounded-sm uppercase">
                                        {selectedResource.content_type}
                                    </span>
                                </div>

                                {/* Description */}
                                <div>
                                    <h4 className="font-serif text-lg text-[#1A1A2E] mb-2">About</h4>
                                    {selectedResource.description ? (
                                        <div
                                            className="font-sans text-[#4A4A68] leading-relaxed [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_a]:text-[#5B2D8E] [&_strong]:text-[#1A1A2E]"
                                            dangerouslySetInnerHTML={{ __html: selectedResource.description }}
                                        />
                                    ) : (
                                        <p className="font-sans text-[#4A4A68]">No description available.</p>
                                    )}
                                </div>

                                {/* Pricing */}
                                <div className="bg-[#F2EBE5] p-6 rounded-sm">
                                    <h4 className="font-serif text-lg text-[#1A1A2E] mb-4">Pricing</h4>
                                    {getPrice(selectedResource) === 0 ? (
                                        <p className="font-serif text-3xl text-[#5B2D8E]">Free</p>
                                    ) : (
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="text-center p-4 bg-white rounded-sm">
                                                <p className="text-xs text-[#4A4A68] uppercase tracking-wider mb-1">Regular Price</p>
                                                <DisplayPrice amount={selectedResource.price} currency={selectedResource.currency} className="font-serif text-3xl text-[#1A1A2E]" />
                                            </div>
                                            <div className="text-center p-4 bg-[#5B2D8E] text-white rounded-sm">
                                                <p className="text-xs uppercase tracking-wider mb-1 text-[#D8B4E2]">Member Price</p>
                                                <DisplayPrice amount={selectedResource.member_price} currency={selectedResource.currency} className="font-serif text-3xl" noteClassName="text-[#D8B4E2]" />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Purchase Section
                                    - required_tier set by backend only when the user's tier is BELOW min_tier → true lock.
                                    - Otherwise `accessible=False` just means the item is paid & unpurchased — show the Buy path. */}
                                {!selectedResource.accessible && selectedResource.required_tier ? (
                                    <div className="text-center p-6 bg-[#F9F6F0] rounded-sm">
                                        <Lock className="w-8 h-8 text-[#5B2D8E] mx-auto mb-2" />
                                        <p className="text-[#4A4A68]">
                                            This resource requires {selectedResource.required_tier} membership or higher.
                                        </p>
                                        <a href="/membership" className="btn-primary mt-4 inline-block">
                                            Upgrade Membership
                                        </a>
                                    </div>
                                ) : (selectedResource.purchased || (getPrice(selectedResource) === 0 && selectedResource.accessible)) ? (
                                    <button
                                        onClick={() => handleAccessResource(selectedResource)}
                                        disabled={accessing === selectedResource.id}
                                        className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60"
                                        data-testid="access-resource-btn"
                                    >
                                        {accessing === selectedResource.id ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Download className="w-4 h-4" />
                                        )}
                                        {selectedResource.purchased ? 'Access Your Resource' : 'Access Free Resource'}
                                    </button>
                                ) : (
                                    <div className="space-y-4">
                                        {!isAuthenticated && (
                                            <div className="p-4 bg-[#F9F6F0] rounded-sm space-y-3">
                                                <div className="flex items-center justify-between">
                                                    <p className="text-sm text-[#5B2D8E] font-medium">Your Information</p>
                                                    <Link
                                                        to={`/login?next=${encodeURIComponent(window.location.pathname)}`}
                                                        className="text-xs text-[#5B2D8E] hover:underline"
                                                        data-testid="library-modal-signin-link"
                                                    >
                                                        Already a client? Sign in →
                                                    </Link>
                                                </div>
                                                <div>
                                                    <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Name</label>
                                                    <input
                                                        type="text"
                                                        value={guestName}
                                                        onChange={(e) => setGuestName(e.target.value)}
                                                        className="w-full"
                                                        placeholder="Your name"
                                                        data-testid="purchase-name"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Your Email *</label>
                                                    <input
                                                        type="email"
                                                        value={guestEmail}
                                                        onChange={(e) => { setGuestEmail(e.target.value); setGuestEmailConflict(null); }}
                                                        onBlur={checkGuestEmail}
                                                        className="w-full"
                                                        placeholder="your@email.com"
                                                        data-testid="purchase-email"
                                                    />
                                                    {guestEmailConflict && (
                                                        <div className="mt-2 p-3 bg-[#5B2D8E]/10 border border-[#5B2D8E]/30 rounded-sm text-sm text-[#1A1A2E]" data-testid="library-email-conflict">
                                                            <strong>An account with this email already exists.</strong>{' '}
                                                            <Link to={`/login?next=${encodeURIComponent(window.location.pathname)}`} className="text-[#5B2D8E] hover:underline">Sign in</Link>{' '}
                                                            to continue, or{' '}
                                                            <Link to="/forgot-password" className="text-[#5B2D8E] hover:underline">reset your password</Link>.
                                                        </div>
                                                    )}
                                                </div>
                                                {/* Seeker membership upsell — the whole point of Phase 2. */}
                                                {seekerUpsellPrice > 0 && (
                                                    <label className="flex items-start gap-3 p-3 bg-white border border-[#5B2D8E]/20 rounded-sm cursor-pointer" data-testid="seeker-upsell-label">
                                                        <input
                                                            type="checkbox"
                                                            checked={wantsSeekerUpsell}
                                                            onChange={(e) => setWantsSeekerUpsell(e.target.checked)}
                                                            className="mt-1"
                                                            data-testid="seeker-upsell-checkbox"
                                                        />
                                                        <div className="flex-1">
                                                            <p className="text-sm text-[#1A1A2E]">
                                                                <strong>Add {seekerUpsellPrice.toFixed(2)}€ to unlock a 1-month Seeker membership.</strong>
                                                            </p>
                                                            <p className="text-xs text-[#4A4A68] mt-1">
                                                                Gets you into the members' area, the forum, and the Connections directory. We'll email you a link to set your password.
                                                            </p>
                                                        </div>
                                                    </label>
                                                )}
                                                <p className="text-xs text-[#4A4A68]">
                                                    We'll email your item after payment. Please check your spam folder if it doesn't arrive within a few minutes.
                                                </p>
                                            </div>
                                        )}
                                        <button
                                            onClick={handlePurchase}
                                            className="btn-primary w-full flex items-center justify-center gap-2"
                                            disabled={purchasing}
                                            data-testid="purchase-btn"
                                        >
                                            {purchasing ? (
                                                <>
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                    Processing...
                                                </>
                                            ) : (
                                                <>
                                                    <ShoppingCart className="w-4 h-4" />
                                                    Buy for <DisplayPrice amount={getPrice(selectedResource)} currency={selectedResource.currency} inline showNote={false} />
                                                    <ArrowRight className="w-4 h-4" />
                                                </>
                                            )}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            <Footer />
        </div>
    );
}
